import React, { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';
import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import { mergeFlatItemsWithLegacy, toFlatImageItem } from '~/components/Common/Images/legacyImageAdapter';
import { FlatImageEditorProps, FlatImageItem } from '~/components/Common/Images/types';

import { useGetPropertyImages } from '~/hooks/apis/accommodation/useGetPropertyImages';
import { useUpdatePropertyImages } from '~/hooks/apis/accommodation/useUpdatePropertyImages';

import { AdminPropertyImageResponse } from '~/services/AccommodationService';

import useAccommodationImageUploadFile from '../useAccommodationImageUploadFile';

interface Props {
  propertyId: string;
  propertyName?: string;
}

function getDisplayOrder(image: Pick<AdminPropertyImageResponse, 'order' | 'manualOrder'>) {
  return image.manualOrder ?? image.order;
}

function comparePropertyImages<T extends Pick<AdminPropertyImageResponse, 'order' | 'manualOrder' | 'imageId'>>(
  left: T,
  right: T,
) {
  const leftDisplayOrder = getDisplayOrder(left);
  const rightDisplayOrder = getDisplayOrder(right);

  if (leftDisplayOrder !== rightDisplayOrder) {
    return leftDisplayOrder - rightDisplayOrder;
  }

  if (left.order !== right.order) {
    return left.order - right.order;
  }

  return left.imageId.localeCompare(right.imageId);
}

function sortPropertyImages<T extends Pick<AdminPropertyImageResponse, 'order' | 'manualOrder' | 'imageId'>>(
  images: Array<T>,
) {
  return [...images].sort(comparePropertyImages);
}

type EditablePropertyImage = AdminPropertyImageResponse & {
  id: string;
  publicImageId: string;
  rowId?: string;
  hidden: boolean;
};

function toEditablePropertyImage(image: AdminPropertyImageResponse): EditablePropertyImage {
  return {
    ...image,
    id: image.imageId,
    publicImageId: image.imageId,
    rowId: image.id,
    hidden: image.hidden ?? false,
  };
}

function applyDenseManualOrder<T extends EditablePropertyImage>(images: Array<T>): Array<T> {
  return images.map((image, index) => ({
    ...image,
    manualOrder: index,
  }));
}

function getSourceLabel(image: EditablePropertyImage) {
  if (image.source !== 'sync') {
    return 'Manual';
  }

  return image.sourceService ?? 'Sync';
}

function renderMetadataChips(image: EditablePropertyImage, images: Array<EditablePropertyImage>) {
  const canonicalImage = image.canonicalImageId
    ? images.find((candidate) => candidate.rowId === image.canonicalImageId)
    : null;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`Source: ${getSourceLabel(image)}`} />
      {image.canonicalImageId && (
        <Chip
          size="small"
          color="warning"
          label={`Duplicate of: ${canonicalImage?.imageId ?? image.canonicalImageId}`}
        />
      )}
    </Stack>
  );
}

function getCanonicalRootRowId(
  image: EditablePropertyImage,
  imagesByRowId: Map<string, EditablePropertyImage>,
): string | null {
  if (!image.rowId) {
    return null;
  }

  let current = image;
  const seen = new Set<string>();

  while (current.canonicalImageId) {
    if (seen.has(current.rowId ?? current.imageId)) {
      return image.rowId;
    }
    seen.add(current.rowId ?? current.imageId);

    const parent = imagesByRowId.get(current.canonicalImageId);
    if (!parent?.rowId) {
      return image.rowId;
    }

    current = parent;
  }

  return current.rowId ?? null;
}

function getDuplicateGroups(images: Array<EditablePropertyImage>) {
  const imagesByRowId = new Map(images.flatMap((image) => (image.rowId ? [[image.rowId, image]] : [])));
  const duplicatesByRootRowId = new Map<string, Array<EditablePropertyImage>>();
  const duplicateRowIds = new Set<string>();

  images.forEach((image) => {
    const rootRowId = getCanonicalRootRowId(image, imagesByRowId);
    if (!rootRowId || !image.rowId || rootRowId === image.rowId) {
      return;
    }

    duplicateRowIds.add(image.rowId);
    duplicatesByRootRowId.set(rootRowId, [...(duplicatesByRootRowId.get(rootRowId) ?? []), image]);
  });

  return { duplicateRowIds, duplicatesByRootRowId };
}

function applyEditorDefaults(image: EditablePropertyImage): EditablePropertyImage {
  return {
    ...image,
    id: image.imageId,
    publicImageId: image.imageId,
    source: image.source ?? 'upload',
    sourceService: image.sourceService ?? null,
    hidden: image.hidden ?? false,
  };
}

function renderDuplicateImages({
  duplicates,
  expanded,
  onToggle,
}: {
  duplicates: Array<EditablePropertyImage>;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (duplicates.length === 0) {
    return null;
  }

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
      <Button
        size="small"
        startIcon={expanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
        onClick={onToggle}
      >
        {duplicates.length} duplicate image{duplicates.length === 1 ? '' : 's'}
      </Button>
      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {duplicates.map((duplicate) => (
            <Stack
              key={duplicate.imageId}
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  aspectRatio: '16 / 9',
                  bgcolor: 'grey.100',
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  flex: '0 0 auto',
                }}
              >
                <Image
                  alt={duplicate.title || duplicate.imageId}
                  publicId={duplicate.imageId}
                  options={{ width: 160 }}
                  style={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
                />
              </Box>
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Typography noWrap variant="body2">
                  {duplicate.imageId}
                </Typography>
                {duplicate.title ? (
                  <Typography color="text.secondary" noWrap variant="caption">
                    {duplicate.title}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function AccommodationPropertyImagesPanel({ propertyId, propertyName }: Props) {
  const { data, isLoading, error } = useGetPropertyImages({
    propertyId,
    enabled: !!propertyId,
  });
  const updatePropertyImagesMutation = useUpdatePropertyImages();
  const [draftImages, setDraftImages] = useState<Array<EditablePropertyImage>>([]);
  const [expandedDuplicateGroups, setExpandedDuplicateGroups] = useState<Set<string>>(new Set());
  const hasInitialisedDraftImagesRef = useRef(false);

  const displayPropertyName = data?.propertyName ?? propertyName ?? 'this property';
  const sortedImages = useMemo(() => sortPropertyImages(draftImages), [draftImages]);
  const handleUploadFile = useAccommodationImageUploadFile(sortedImages.length);
  const sortedPersistedImages = useMemo(
    () => sortPropertyImages((data?.images ?? []).map(toEditablePropertyImage)),
    [data?.images],
  );
  const hasChanges = !isEqual(sortedImages, sortedPersistedImages);
  const hiddenImagesCount = sortedImages.filter((image) => image.hidden).length;
  const { duplicateRowIds, duplicatesByRootRowId } = useMemo(() => getDuplicateGroups(sortedImages), [sortedImages]);
  const visibleImages = useMemo(
    () => sortedImages.filter((image) => !image.rowId || !duplicateRowIds.has(image.rowId)),
    [duplicateRowIds, sortedImages],
  );
  const duplicateImagesCount = sortedImages.length - visibleImages.length;

  useEffect(() => {
    if (!data) {
      return;
    }

    // Resync the draft after the mutation refreshes cached data, but avoid clobbering unsaved edits.
    if (!hasInitialisedDraftImagesRef.current || !hasChanges) {
      setDraftImages(data.images.map(toEditablePropertyImage));
      hasInitialisedDraftImagesRef.current = true;
    }
  }, [data, hasChanges]);

  const handleItemsChange = (
    nextItems: Array<FlatImageItem>,
    meta?: Parameters<NonNullable<FlatImageEditorProps['onItemsChange']>>[1],
  ) => {
    setDraftImages((currentImages) => {
      const currentSortedImages = sortPropertyImages(currentImages);
      const currentGroups = getDuplicateGroups(currentSortedImages);
      const currentVisibleImages = currentSortedImages.filter(
        (image) => !image.rowId || !currentGroups.duplicateRowIds.has(image.rowId),
      );
      const nextVisibleImages = mergeFlatItemsWithLegacy(currentVisibleImages, nextItems).map(applyEditorDefaults);

      if (meta?.reason !== 'reorder') {
        const nextVisibleByImageId = new Map(nextVisibleImages.map((image) => [image.imageId, image]));
        const currentImageIds = new Set(currentImages.map((image) => image.imageId));
        return [
          ...currentImages.map((image) => nextVisibleByImageId.get(image.imageId) ?? image),
          ...nextVisibleImages.filter((image) => !currentImageIds.has(image.imageId)),
        ];
      }

      const groupedImages = nextVisibleImages.flatMap((image) => [
        image,
        ...(image.rowId ? (currentGroups.duplicatesByRootRowId.get(image.rowId) ?? []) : []),
      ]);
      return applyDenseManualOrder(groupedImages);
    });
  };

  const handleSave = async () => {
    try {
      await updatePropertyImagesMutation.mutateAsync({
        propertyId,
        images: sortedImages.map((image) => ({
          imageId: image.imageId,
          title: image.title,
          hidden: image.hidden,
        })),
      });
    } catch {
      // Mutation-level error handling already surfaces feedback to the user.
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load property images: {error.message}</Alert>;
  }

  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary">
        No internal property images are currently stored for {displayPropertyName}.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {sortedImages.length} persisted property images
          {hiddenImagesCount > 0 ? ` (${hiddenImagesCount} hidden)` : ''}.
          {duplicateImagesCount > 0 ? ` ${duplicateImagesCount} duplicates grouped.` : ''}
        </Typography>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || updatePropertyImagesMutation.isPending}
        >
          {updatePropertyImagesMutation.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </Stack>

      <Box>
        <FlatImageEditor
          deleteActionLabel="Delete image"
          descriptionLabel="Title"
          disabled={updatePropertyImagesMutation.isPending}
          emptyStateMessage={`No internal property images are currently stored for ${displayPropertyName}.`}
          items={visibleImages.map((image) => toFlatImageItem(image))}
          uploadLabel="Upload images"
          renderItemActions={(item) => {
            const image = sortedImages.find((currentImage) => currentImage.imageId === String(item.id));

            return image ? renderMetadataChips(image, sortedImages) : null;
          }}
          renderItemDetails={(item) => {
            const image = visibleImages.find((currentImage) => currentImage.imageId === String(item.id));
            const groupKey = image?.rowId;
            const duplicates = groupKey ? (duplicatesByRootRowId.get(groupKey) ?? []) : [];

            return groupKey
              ? renderDuplicateImages({
                  duplicates,
                  expanded: expandedDuplicateGroups.has(groupKey),
                  onToggle: () =>
                    setExpandedDuplicateGroups((current) => {
                      const next = new Set(current);
                      if (next.has(groupKey)) {
                        next.delete(groupKey);
                      } else {
                        next.add(groupKey);
                      }
                      return next;
                    }),
                })
              : null;
          }}
          reorderHiddenToEndOnToggle={false}
          showDeleteAction={false}
          showDescriptionField
          showHiddenToggle
          showReorderAction
          onItemsChange={handleItemsChange}
          onUploadFile={handleUploadFile}
        />
      </Box>
    </Stack>
  );
}
